import { Link, createFileRoute } from "@tanstack/react-router";
import { type ChangeEventHandler, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import ArrowBack from "~icons/material-symbols/arrow-back.svg";
import { css } from "../../../../../styled-system/css";
import { useGetMyCompanies } from "../../../../libs/admin/hooks";
import { getQRCodeURL } from "../../../../libs/ps";
import {
  buildTimestampedFilename,
  downloadCsvFile,
} from "../../../../libs/utils/csv";
import { QR_CODE_TAG_MAX_LENGTH } from "../../../../libs/utils/qr-code-tag";
import { button } from "../../../../styles/buttonStyles";
import { contentWrapper } from "../../../../styles/contentWrapper";
import {
  inlineCode,
  instructionsContent,
  instructionsDetails,
  instructionsHeading,
  instructionsParagraph,
  instructionsSubheading,
  instructionsSummary,
  instructionsSummaryText,
} from "../../../../styles/InstructionStyles";
import {
  pageTitle,
  sectionTitle,
  sectionWrapper,
  titleWrapper,
} from "../../../../styles/sectionStyles";
import {
  table,
  tableCell,
  tableHead,
  tableHeadCell,
  tableRow,
  tableWrapper,
} from "../../../../styles/tableStyles";
import {
  type ParseResult,
  type StoreLookup,
  buildResultCsv,
  parseCsv,
  validateStores,
} from "./-csv";

const rawCushionServiceId = import.meta.env.VITE_CUSHION_SERVICE_ID;
const cushionServiceId =
  typeof rawCushionServiceId === "string" &&
  rawCushionServiceId.trim().length > 0
    ? rawCushionServiceId.trim()
    : null;

const buildQrCodeUrl = (storeSlug: string, qrTag: string): string => {
  if (!cushionServiceId) return "";
  const tag = qrTag.length > 0 ? `?t=${qrTag}` : "";
  return getQRCodeURL(cushionServiceId, `/s/${storeSlug}${tag}`);
};

export const Route = createFileRoute("/admin/companies/bulkCreateQrcodes/")({
  component: RouteComponent,
});

function RouteComponent() {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState("");
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const {
    data: myCompanies,
    isLoading: isCompaniesLoading,
    isError: isCompaniesError,
  } = useGetMyCompanies();

  const storeLookup: StoreLookup = useMemo(() => {
    const map = new Map<string, string>();
    for (const cws of myCompanies ?? []) {
      for (const s of cws.stores ?? []) {
        if (s.slug) map.set(s.slug, s.name);
      }
    }
    return map;
  }, [myCompanies]);

  const validatedResult = useMemo(() => {
    if (!parseResult) return null;
    if (isCompaniesLoading || !myCompanies) return null;
    return validateStores(parseResult, storeLookup);
  }, [parseResult, storeLookup, isCompaniesLoading, myCompanies]);

  const handleFileChange: ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const result = await parseCsv(text);
      setParseResult(result);
      setFileName(file.name);
      if (result.rows.length === 0 && result.errors.length === 0) {
        toast("データ行がありません", { icon: "ℹ️" });
      }
    } catch (err) {
      console.error("CSV parsing failed", err);
      toast.error(
        err instanceof Error ? err.message : "CSVの読み込みに失敗しました",
      );
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const errorCount = validatedResult?.errors.length ?? 0;
  const rowCount = validatedResult?.rows.length ?? 0;
  const canApply =
    !!validatedResult && rowCount > 0 && errorCount === 0 && !!cushionServiceId;

  const handleDownloadResult = () => {
    if (!canApply || !validatedResult) return;
    const csv = buildResultCsv(validatedResult.rows, buildQrCodeUrl);
    downloadCsvFile(
      buildTimestampedFilename(fileName || "result", "result", "csv"),
      csv,
    );
  };

  const handleDownloadTemplate = () => {
    downloadCsvFile("bulk_create_qrcodes_template.csv", "store_slug,qr_tag\n");
  };

  const shownErrors = validatedResult?.errors.slice(0, 10) ?? [];
  const isValidating =
    !!parseResult && (isCompaniesLoading || !myCompanies) && !isCompaniesError;

  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>
          <Link to="/admin/companies">
            <ArrowBack />
          </Link>
          店舗QR一括作成
        </h1>
      </div>
      <div
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        })}
      >
        <div className={sectionWrapper()}>
          <div className={sectionTitle()}>テンプレートをダウンロード</div>
          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            })}
          >
            <p>CSVテンプレートをダウンロードできます。</p>
            <div>
              <button
                type="button"
                onClick={handleDownloadTemplate}
                className={button()}
              >
                CSVテンプレートをダウンロード
              </button>
            </div>
          </div>
        </div>

        <div className={sectionWrapper()}>
          <div className={sectionTitle()}>操作方法</div>
          <Instructions />
        </div>

        <div className={sectionWrapper()}>
          <div className={sectionTitle()}>データを読み込む</div>
          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              alignItems: "flex-start",
            })}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className={button()}
            >
              CSVファイルを読み込む
            </button>
            {isValidating && (
              <span className={css({ color: "text.secondary" })}>
                店舗情報を確認中です…
              </span>
            )}
            {isCompaniesError && (
              <span className={css({ color: "text.accentError" })}>
                店舗情報の取得に失敗しました。再読み込みしてください。
              </span>
            )}
            {errorCount > 0 && validatedResult && (
              <div
                className={css({
                  color: "text.accentError",
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                })}
              >
                <span>{errorCount}件のエラーがあります:</span>
                <ul className={css({ listStyle: "disc", pl: "2em" })}>
                  {shownErrors.map((e, i) => (
                    <li key={`${e.row}-${e.col}-${i}`}>
                      行 {e.row} / {e.col}: {e.message}
                    </li>
                  ))}
                </ul>
                {errorCount > shownErrors.length && (
                  <span>ほか {errorCount - shownErrors.length} 件</span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className={sectionWrapper()}>
          <div className={sectionTitle()}>ダウンロード</div>
          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            })}
          >
            {!cushionServiceId && (
              <span className={css({ color: "text.accentError" })}>
                CUSHION_SERVICE_ID
                が未設定です。管理者にお問い合わせください。
              </span>
            )}
            {validatedResult && errorCount === 0 && rowCount > 0 && (
              <span className={css({ color: "text.secondary" })}>
                {rowCount}件分のURL付きCSVをダウンロードできます。
              </span>
            )}
            <button
              type="button"
              onClick={handleDownloadResult}
              disabled={!canApply}
              className={button({ color: "primary" })}
            >
              URL付きCSVをダウンロード
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const csvFields = [
  {
    name: "store_slug",
    label: "店舗QRコード識別子",
    example: "ABCDefgh1234",
    note: "事業者一覧からダウンロードした店舗一覧CSVの店舗ID列で確認できます。",
  },
  {
    name: "qr_tag",
    label: "QRコードタグ",
    example: "1番レジ",
    note: `空欄可。${QR_CODE_TAG_MAX_LENGTH}文字以内。同一店舗・同一内容の行を複数含めても構いません。`,
  },
] as const;

function Instructions() {
  return (
    <details className={instructionsDetails()}>
      <summary className={instructionsSummary()}>
        <span className={instructionsSummaryText()}>操作マニュアル</span>
      </summary>
      <div className={instructionsContent()}>
        <h2 className={instructionsHeading()}>店舗QR一括作成について</h2>
        <p className={instructionsParagraph()}>
          CSVを読み込むと、各店舗の店舗QR用URLを追加したURL付きCSVをダウンロードできます。生成された
          <code className={inlineCode()}>url</code>{" "}
          列にURLが入ります。
        </p>

        <h3 className={instructionsSubheading()}>CSVファイルの項目説明</h3>
        <div className={tableWrapper()}>
          <table className={table()}>
            <thead className={tableHead()}>
              <tr>
                <th className={tableHeadCell()}>CSV項目名</th>
                <th className={tableHeadCell()}>日本語項目名</th>
                <th className={tableHeadCell()}>入力例</th>
                <th className={tableHeadCell()}>備考</th>
              </tr>
            </thead>
            <tbody>
              {csvFields.map((field) => (
                <tr key={field.name} className={tableRow()}>
                  <td className={tableCell()}>{field.name}</td>
                  <td className={tableCell()}>{field.label}</td>
                  <td className={tableCell()}>
                    <pre>{field.example}</pre>
                  </td>
                  <td className={tableCell()}>{field.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </details>
  );
}
